import { Typography, Container, Divider, makeStyles } from '@material-ui/core';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import Link from '../../components/link';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';
import Seo from '../../components/seo';
import FeatureBlock from './_feature-block';

const useStyles = makeStyles(theme => ({
  featureHeading: {
    marginBottom: theme.spacing(1),
  },
}));

const NetworkSecurityPage: React.FC = () => {
  const classes = useStyles();

  const title = 'Network security';
  const description =
    'Automatically generate strict network policies with each deployment and achieve zero-trust security on day zero.';

  return (
    <PageLayout>
      <Seo title={title} description={description} />

      <Section variant="hero-green">
        <Typography variant="h1" align="center">
          {title}
        </Typography>
        <Typography variant="subtitle1" align="center">
          {description}
        </Typography>
      </Section>

      <FeatureBlock
        image={
          <StaticImage
            src="../../images/icons/lg/network-policies.svg"
            alt="Network policies"
            loading="eager"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Dynamic network policies
        </Typography>
        <Typography variant="body1" paragraph>
          For years software teams have relied on the walled garden approach to network security due
          to the increasing complexity of distributed applications. But increasing cyber threats
          make half-baked security policies a risky proposition for cloud-native applications.
        </Typography>
        <Typography variant="body1" paragraph>
          Using the same dependency management tactics that power service discovery and dependency
          injection, Architect is able to automatically generate strict network policies with every
          deployment. Only the services designed to talk to one another should be able to connect,
          ensuring you have a zero-trust cloud from day zero.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={
          <StaticImage
            src="../../images/icons/lg/transparent-encryption.svg"
            alt="Transparent encryption"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Transparent encryption
        </Typography>
        <Typography variant="body1" paragraph>
          The traffic flowing between services inside your cloud environment is often more sensitive
          than the traffic coming from the outside world. Set up and maintainance has historically
          been cumbersome for private, internal services, but not with Architect.
        </Typography>
        <Typography variant="body1" paragraph>
          Architects automated service mesh configuration allows it to seamlessly instrument TLS
          in-transit for internal network calls. Applications are automatically configured to flow
          traffic through secure, Envoy sidecars which take full responsbility for encryption and
          decryption of network calls.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        image={<StaticImage src="../../images/icons/lg/service-mesh.svg" alt="Service mesh" />}
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Service mesh enforcement
        </Typography>
        <Typography variant="body1" paragraph>
          Service meshes are powerful tools to broker secure networking within cloud environments,
          but their security benefits aren't limited simply to encryption.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect's dynamic network policy generation works seamlessly with the service mesh to
          enforce network policies. After all, writing network policies means nothing without the
          suitable tools to enforce those policies.
        </Typography>
      </FeatureBlock>
    </PageLayout>
  );
};

export default NetworkSecurityPage;
